import {
  Button,
  CardMedia,
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Switch,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  IconButton,
  Box,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import React, { useState, useContext, useEffect } from 'react';
import ItemService from 'services/ItemService';
import { ShopContext } from 'Context/ShopContext';
import { CommonContext } from 'Context/CommonContext';
import { AlertMessagesContext } from 'react-alert-messages';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CONFIGS, DAYS_OF_WEEK, CATEGORIES_TAG_SUGGESTIONS, ORDER_TYPE_LIST } from 'const';
import NumberField from 'components/library/NumberField';
import ShopsHelper from 'helpers/ShopsHelper';

export default function EditCategoryDialog(props) {
  const { t: tCommon } = useTranslation('common');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { kitchens, getKitchens } = useContext(CommonContext);

  const { handleClose, category, onSuccess } = props;

  const [categoryName, setCategoryName] = useState(category?.name || '');
  const [categoryDesc, setCategoryDesc] = useState(category?.description || null);
  const [kitchenIp, setKitchenIp] = useState(category?.kitchen_ip || null);
  const [kitchenName, setKitchenName] = useState(category?.kitchen_name || null);
  const [kitchenId, setKitchenId] = useState(category?.kitchen?.id || category?.kitchen || '');
  const [imageData, setImageData] = useState('');
  const [isAvailable, setIsAvailable] = useState(category?.is_available || false);
  const [order, setOrder] = useState(category?.order || null);
  const [isNotifyForOnlineOrder, setIsNotifyForOnlineOrder] = useState(category?.is_notify_for_online_order || false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAvailableOnCart, setIsAvailableOnCart] = useState(category?.is_available_on_cart || false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [timeRanges, setTimeRanges] = useState([{ from: null, to: null }]);
  const [tags, setTags] = useState([{ key: '', value: '' }]);
  const [isEnableKotPrint, setIsEnableKotPrint] = useState(category?.config?.kot_settings?.is_enable_kot_print ?? true);
  const [isEnableForCaptain, setIsEnableForCaptain] = useState(
    category?.config?.kot_settings?.is_enable_for_captain ?? true
  );
  const [orderTypes, setOrderTypes] = useState({
    DINE_IN: category?.config?.kot_settings?.order_types?.DINE_IN ?? true,
    TAKE_AWAY: category?.config?.kot_settings?.order_types?.TAKE_AWAY ?? true,
    DELIVERY: category?.config?.kot_settings?.order_types?.DELIVERY ?? true,
    ONLINE: category?.config?.kot_settings?.order_types?.ONLINE ?? true,
  });

  const handleDayChange = (_, newDays) => setSelectedDays(newDays);

  const handleTimeRangeChange = (idx, field, val) =>
    setTimeRanges((ranges) => {
      const copy = [...ranges];
      copy[idx] = { ...copy[idx], [field]: val };
      return copy;
    });

  const addRangeRow = () => setTimeRanges((ranges) => [...ranges, { from: null, to: null }]);

  const removeRangeRow = (idx) => setTimeRanges((ranges) => ranges.filter((_, i) => i !== idx));

  const handleTagChange = (index, field, value) => {
    const updated = [...tags];
    updated[index][field] = value;
    setTags(updated);
  };

  const handleAddTagRow = () => setTags((prev) => [...prev, { key: '', value: '' }]);
  const handleDeleteTagRow = (index) => setTags((prev) => prev.filter((_, i) => i !== index));

  const getKeySuggestions = (input) => {
    return CATEGORIES_TAG_SUGGESTIONS.map((s) => s.key).filter((k) =>
      k.toLowerCase().includes((input || '').toLowerCase())
    );
  };

  const getValueSuggestions = (key) => {
    const found = CATEGORIES_TAG_SUGGESTIONS.find((s) => s.key === key);
    return found ? found.values : [];
  };

  const tagsObject = (tagsArray) => {
    const obj = {};
    tagsArray.forEach(({ key, value }) => {
      const k = (key ?? '').trim();
      if (!k) return;

      const v = value ?? '';
      if (Object.prototype.hasOwnProperty.call(obj, k)) {
        if (Array.isArray(obj[k])) {
          obj[k].push(v);
        } else {
          obj[k] = [obj[k], v];
        }
      } else {
        obj[k] = v;
      }
    });
    return obj;
  };

  useEffect(() => {
    setCategoryName(category?.name || '');
    setCategoryDesc(category?.description || '');
    setKitchenIp(category?.kitchen_ip || null);
    setKitchenId(category?.kitchen?.id || category?.kitchen || '');
    setOrder(category?.order || null);
    setIsAvailable(category?.is_available || false);
    setIsAvailableOnCart(category?.is_available_on_cart || false);
    setIsNotifyForOnlineOrder(category?.is_notify_for_online_order || false);
    if (category.available_days) {
      setSelectedDays(category.available_days.split(','));
    }

    if (category.available_time_range) {
      const parsed = category.available_time_range.split(',').map((pair) => {
        const [from, to] = pair.split('-');
        return {
          from: new Date(`1970-01-01T${from}:00`),
          to: new Date(`1970-01-01T${to}:00`),
        };
      });
      setTimeRanges(parsed);
    }
    const existingTags = [];
    const tagsObj = category?.config?.tags || null;
    if (tagsObj && typeof tagsObj === 'object') {
      Object.entries(tagsObj).forEach(([k, v]) => {
        if (Array.isArray(v)) {
          v.forEach((val) => existingTags.push({ key: k, value: val }));
        } else {
          existingTags.push({ key: k, value: v });
        }
      });
    }
    setTags(existingTags.length ? existingTags : [{ key: '', value: '' }]);
    setIsEnableKotPrint(category?.config?.kot_settings?.is_enable_kot_print ?? true);
    setIsEnableForCaptain(category?.config?.kot_settings?.is_enable_for_captain ?? true);

    const orderTypesConfig = category?.config?.kot_settings?.order_types || {};

    setOrderTypes({
      DINE_IN: orderTypesConfig.DINE_IN ?? true,
      TAKE_AWAY: orderTypesConfig.TAKE_AWAY ?? true,
      DELIVERY: orderTypesConfig.DELIVERY ?? true,
      ONLINE: orderTypesConfig.ONLINE ?? true,
    });
  }, [category]);

  const uploadImage = async (image, categId) => {
    const reader = new FileReader();
    reader.readAsDataURL(image);
    reader.onloadend = async () => {
      const data = { image: reader.result };
      await ItemService.uploadCategoryImage(data, categId);
    };
  };

  const handleEditCategory = async () => {
    const hasAnySchedule = selectedDays.length > 0 || timeRanges.some(({ from, to }) => from != null || to != null);

    let daysStr = null;
    let timeRangesStr = null;

    if (hasAnySchedule) {
      for (const { from, to } of timeRanges) {
        if (!from || !to) {
          postAlertMessage({ text: 'Please complete all time ranges.', type: 'failed' });
          return;
        }
        if (from >= to) {
          postAlertMessage({ text: 'Start must be before End.', type: 'failed' });
          return;
        }
      }

      daysStr = selectedDays.join(',');

      timeRangesStr = timeRanges
        .map(({ from, to }) => {
          const pad = (n) => String(n).padStart(2, '0');
          const fH = pad(from.getHours()),
            fM = pad(from.getMinutes());
          const tH = pad(to.getHours()),
            tM = pad(to.getMinutes());
          return `${fH}:${fM}-${tH}:${tM}`;
        })
        .join(',');
    }

    setIsUpdating(true);

    try {
      const data = {
        name: categoryName,
      };

      data.description = categoryDesc ?? null;
      data.kitchen_ip = kitchenIp ? kitchenIp : null;
      data.kitchen_name = kitchenName ? kitchenName : null;
      data.kitchen_id = kitchenId ? Number(kitchenId) : null;
      data.is_available = isAvailable ?? null;
      data.is_available_on_cart = isAvailableOnCart ?? null;
      data.is_notify_for_online_order = isNotifyForOnlineOrder ?? null;
      data.order = order ? order : null;
      data.available_days = daysStr || null;
      data.available_time_range = timeRangesStr || null;
      data.available_days = daysStr || null;
      data.available_time_range = timeRangesStr || null;
      data.config =
        {
          ...category?.config,
          kot_settings: {
            ...category?.config?.kot_settings,
            is_enable_kot_print: isEnableKotPrint,
            is_enable_for_captain: isEnableForCaptain,
            order_types: orderTypes,
          },
        } || {};

      const filteredTags = tags.filter((t) => (t.key ?? '').trim() !== '');
      const tagsObj = tagsObject(filteredTags);
      if (Object.keys(tagsObj).length) {
        data.config = { ...(category?.config || {}), tags: tagsObj };
      }

      const resp = await ItemService.editItemCategory(data, category.id);
      const updatedCategory = resp?.data ?? resp;

      if (imageData && imageData.size && imageData.size < CONFIGS.MAX_IMAGE_SIZE) {
        await uploadImage(imageData, category.id);
      }

      postAlertMessage({ text: 'Category updated successfully', type: 'success' });

      onSuccess(updatedCategory);
      handleClose();
    } catch (err) {
      console.error('Error Updating Category:', err);
      postAlertMessage({ text: err.message, type: 'failed' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleChangeKotPrint = (value) => {
    setIsEnableKotPrint(value);
    if (value) {
      setOrderTypes({
        DINE_IN: true,
        TAKE_AWAY: true,
        DELIVERY: true,
        ONLINE: true,
      });
      return;
    }
    setOrderTypes({
      DINE_IN: false,
      TAKE_AWAY: false,
      DELIVERY: false,
      ONLINE: false,
    });
  };

  const handleEnableForCaptain = (value) => {
    setIsEnableForCaptain(value);
  };

  const handleOrderTypeChange = (key) => {
    setOrderTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };


  useEffect(() => {
    getKitchens();
  }, [getKitchens]);

  return (
    <div className={styles.popUp} style={{ zIndex: 1400 }}>
      <div className={styles.contentWrapper}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Edit Category')}</h2>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <div className={styles.inputSec}>
          <Grid container alignItems="top" spacing={1}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Category Name')}
                variant="outlined"
                size="small"
                fullWidth
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={tCommon('Category Description')}
                variant="outlined"
                size="small"
                fullWidth
                value={categoryDesc}
                onChange={(e) => setCategoryDesc(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField
                label={tCommon('Display Order')}
                value={order}
                onChange={(e) => {
                  setOrder(e.target.value);
                }}
              />
            </Grid>
            {(shop.kot_lite_validity || shop.kot_validity) && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label={tCommon('Select Kitchen') || 'Select Kitchen'}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={kitchenId}
                    onChange={(e) => setKitchenId(e.target.value)}
                    SelectProps={{
                      MenuProps: {
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        PaperProps: { style: { maxHeight: 350 } },
                        style: { zIndex: 10002 },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>{tCommon('None') || 'None'}</em>
                    </MenuItem>
                    {(kitchens || []).map((k) => {
                      const chefName = k.chef?.name || (typeof k.chef === 'string' && k.chef !== '-' ? k.chef : '');
                      return (
                        <MenuItem key={k.id} value={k.id}>
                          {k.name
                            ? chefName
                              ? `${k.name} (Chef: ${chefName})`
                              : k.name
                            : chefName
                            ? `Chef: ${chefName}`
                            : `Kitchen ${k.id}`}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label={tCommon('Kitchen Name')}
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={kitchenName}
                    onChange={(e) => {
                      setKitchenName(e.target.value);
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>

          {(shop.kot_lite_validity || shop.kot_validity) && (
            <Grid container m={1} className={styles.subContainer} style={{ margin: '10px 0' }}>
              <Grid p={1}>
                <h3 className={styles.inputLabel}>{tCommon('KOT Settings')}</h3>
              </Grid>

              <Grid item container xs={12} component={Paper}>
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', alignItems: 'center', p: 1.25, justifyContent: 'space-between' }}
                >
                  <span>{tCommon('Enable For Captain')}</span>
                  <Switch
                    checked={isEnableForCaptain}
                    color="primary"
                    onChange={() => {
                      handleEnableForCaptain(!isEnableForCaptain);
                    }}
                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  sx={{ display: 'flex', alignItems: 'center', p: 1.25, justifyContent: 'space-between' }}
                >
                  <span>{tCommon('Enable KOT print')}</span>
                  <Switch
                    checked={isEnableKotPrint}
                    color="primary"
                    onChange={() => {
                      handleChangeKotPrint(!isEnableKotPrint);
                    }}
                    slotProps={{ input: { 'aria-label': 'controlled' } }}
                  />
                </Grid>
                {isEnableKotPrint && (
                  <Grid container className={styles.labelContainer} spacing={1} sx={{ pb: 1.25 }}>
                    <Grid item xs={12} sm={6}>
                      <span>{tCommon('Kitchen IP Address')}</span>
                    </Grid>
                    <Grid item xs={12} sm={6} style={{ padding: '10px 0' }}>
                      <TextField
                        label={tCommon('Kitchen IP Address')}
                        variant="outlined"
                        fullWidth
                        value={kitchenIp}
                        onChange={(e) => {
                          setKitchenIp(e.target.value);
                        }}
                      />
                    </Grid>
                    {ORDER_TYPE_LIST.map(({ key, label, icon }) => (
                      <Grid item xs={12} sm={6} key={key}>
                        <Paper
                          key={key}
                          className={`${styles.option} ${orderTypes[key] ? styles.optionActive : ''}`}
                          elevation={0}
                          component="label"
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={orderTypes[key]}
                                onChange={() => handleOrderTypeChange(key)}
                                className={styles.checkbox}
                              />
                            }
                            label={
                              <div className={styles.labelContent}>
                                <span className={styles.icon}>{icon}</span>
                                <span className={styles.label}>{label}</span>
                              </div>
                            }
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}

          <Grid container className={styles.inputRow} alignItems="center">
            <Grid item xs={6} sm={4}>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox color="primary" checked={isAvailable} onChange={() => setIsAvailable(!isAvailable)} />
                }
                label={tCommon('Available for sale')}
                labelPlacement="end"
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    color="primary"
                    checked={isAvailableOnCart}
                    onChange={() => setIsAvailableOnCart(!isAvailableOnCart)}
                  />
                }
                label={tCommon('Available On Cart')}
                labelPlacement="end"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                value="end"
                control={
                  <Checkbox
                    color="primary"
                    checked={isNotifyForOnlineOrder}
                    onChange={() => setIsNotifyForOnlineOrder(!isNotifyForOnlineOrder)}
                  />
                }
                label={tCommon('Online Order Notification')}
                labelPlacement="end"
              />
            </Grid>
          </Grid>
          {shop && shop.image_support_validity && (
            <Grid container item xs={12}>
              <Grid item xs={6}>
                <Button variant="contained" className={styles.uploadBtn}>
                  <label htmlFor="upload">{imageData ? tCommon('Change Image') : tCommon('Choose Image')}</label>
                </Button>
              </Grid>
              <Grid item xs={6} sm={6}>
                <div style={{ fontSize: 16, paddingBottom: 5 }}>{tCommon('150px * 150px size recommended')}</div>
                <div className={styles.imageContainer}>
                  <CardMedia
                    image={imageData ? URL.createObjectURL(imageData) : category?.image}
                    className={styles.image}
                  />
                </div>
                {imageData && imageData.size > CONFIGS.MAX_IMAGE_SIZE && (
                  <div className={styles.imageSizeWarning}>
                    {tCommon('*Image size exceeds maximum limit. Upload file size less than {{kb}}KB', { kb: 100 })}
                  </div>
                )}
                <input
                  accept="image/*"
                  type="file"
                  hidden
                  id="upload"
                  onChange={(e) => setImageData(e.target.files[0])}
                />
              </Grid>
            </Grid>
          )}

          {ShopsHelper.hasPremiumAccess(shop) && (
            <Grid
              container
              className={styles.inputRow}
              alignItems="center"
              style={{ marginTop: '10px', paddingLeft: '0' }}
            >
              <Grid container className={styles.subContainer} style={{ marginTop: '24px' }}>
                <Grid p={1}>
                  <h3 className={styles.inputLabel}>{tCommon('Category tags')}</h3>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <strong>{tCommon('Key')}</strong>
                          </TableCell>
                          <TableCell>
                            <strong>{tCommon('Value')}</strong>
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tags.map((row, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={getKeySuggestions(row.key)}
                                value={row.key}
                                onChange={(e, newValue) => handleTagChange(index, 'key', newValue || '')}
                                onInputChange={(e, newValue) => handleTagChange(index, 'key', newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder={tCommon('Key')} variant="outlined" />
                                )}
                              />
                            </TableCell>

                            <TableCell>
                              <Autocomplete
                                freeSolo
                                size="small"
                                options={getValueSuggestions(row.key)}
                                value={row.value}
                                onChange={(e, newValue) => handleTagChange(index, 'value', newValue || '')}
                                onInputChange={(e, newValue) => handleTagChange(index, 'value', newValue)}
                                renderInput={(params) => (
                                  <TextField {...params} placeholder={tCommon('Value')} variant="outlined" />
                                )}
                              />
                            </TableCell>

                            <TableCell align="right">
                              <IconButton
                                color="error"
                                size="small"
                                onClick={() => handleDeleteTagRow(index)}
                                disabled={tags.length === 1}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <Box sx={{ p: 1 }}>
                        <Button
                          startIcon={<LibraryAddIcon />}
                          onClick={handleAddTagRow}
                          size="small"
                          variant="outlined"
                        >
                          {tCommon('Add New Tag')}
                        </Button>
                      </Box>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Grid>
          )}
          {/* ── Category Schedule ── */}
          {ShopsHelper.hasPremiumAccess(shop) && (
            <Grid
              container
              className={styles.inputRow}
              alignItems="center"
              style={{ marginTop: '10px', paddingLeft: '0' }}
            >
              <Grid container className={styles.subContainer}>
                <Grid p={1}>
                  <h3 className={styles.inputLabel}>{tCommon('Category Schedule')}</h3>
                </Grid>
                <Grid container className={styles.inputRow} alignItems="center">
                  <Grid item xs={12} sm={2} className={styles.inputLabelContainer}>
                    <h3 className={styles.inputSubLabel}>{tCommon('Available Days')}</h3>
                  </Grid>
                  <Grid item xs={12} sm={10}>
                    <ToggleButtonGroup
                      value={selectedDays}
                      onChange={handleDayChange}
                      aria-label="days"
                      size="small"
                      fullWidth
                    >
                      {Object.values(DAYS_OF_WEEK).map((day) => (
                        <ToggleButton
                          key={day}
                          value={day}
                          aria-label={day}
                          style={{
                            padding: '6px',
                            color: selectedDays.includes(day) ? '#fff' : '#333',
                            backgroundColor: selectedDays.includes(day) ? '#0b8300a8' : '#ffffff00',
                            border: '1px solid #ccc',
                            borderRadius: 3,
                            fontWeight: 500,
                          }}
                        >
                          {day}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Grid>
                </Grid>

                {timeRanges.map((tr, idx) => (
                  <Grid container className={styles.inputRow} alignItems="center" key={idx}>
                    <Grid item xs={12} sm={2} className={styles.inputLabelContainer}>
                      <h3 className={styles.inputSubLabel}>{idx === 0 ? tCommon('Available Time') : ''}</h3>
                    </Grid>
                    <Grid item xs={12} sm={10}>
                      <Grid container spacing={1} alignItems="center">
                        <Grid item xs={5}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                              label={tCommon('Start Time')}
                              value={tr.from}
                              format="hh:mma"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: 40,
                                      fontSize: '0.875rem',
                                      alignItems: 'center',
                                    },
                                  },
                                },
                              }}
                              onChange={(val) => handleTimeRangeChange(idx, 'from', val)}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={5}>
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <TimePicker
                              label={tCommon('End Time')}
                              value={tr.to}
                              format="hh:mma"
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  InputProps: {
                                    sx: {
                                      height: 40,
                                      fontSize: '0.875rem',
                                      alignItems: 'center',
                                    },
                                  },
                                },
                              }}
                              onChange={(val) => handleTimeRangeChange(idx, 'to', val)}
                            />
                          </LocalizationProvider>
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            onClick={() => removeRangeRow(idx)}
                            disabled={timeRanges.length === 1}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                ))}

                <Grid container>
                  <Grid item xs={12} sm={12} style={{ padding: '10px 0' }}>
                    <Button size="small" variant="outlined" startIcon={<LibraryAddIcon />} onClick={addRangeRow}>
                      {tCommon('Add New Time Schedule')}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
        </div>
        <div className={styles.footSec}>
          <DialogActions>
            <Button variant="contained" color="secondary" onClick={handleClose} className={styles.closeBtn}>
              {tCommon('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!categoryName || isUpdating}
              onClick={handleEditCategory}
            >
              {isUpdating ? tCommon('Loading...') : tCommon('Update')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
